// src/pages/Projects/ProjectList.jsx
// -----------------------------------------------------------------------------
// Lists all projects and provides full CRUD capabilities using reusable components.
// Matches the brand design theme of AFix.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import projectService from '../../api/projectService';
import clientOrgService from '../../api/clientOrgService';
import projectEnvironmentService from '../../api/projectEnvironmentService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth, ROLES } from '../../context/AuthContext';

export default function ProjectList() {
  const navigate = useNavigate();
  // Requirement #9: only Admin / PM may add or change projects (API enforces it too)
  const { hasRole } = useAuth();
  const canManage = hasRole(ROLES.ADMIN, ROLES.PROJECT_MANAGER);
  const [clientOrgs, setClientOrgs] = useState([]);
  const [environments, setEnvironments] = useState([]);

  // Fetch client organizations and environments to populate dropdown options
  useEffect(() => {
    clientOrgService
      .getAll()
      .then((res) => setClientOrgs(res.data || []))
      .catch(() => { });
    projectEnvironmentService
      .getAll()
      .then((res) => setEnvironments(res.data || []))
      .catch(() => { });
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(projectService, {
    idKey: 'projectID',
    itemName: 'Project',
    initialSort: { key: 'projectID', direction: 'asc' },
  });

  // Schema configuration for Project_TBL dynamically populated with options
  const projectFields = useMemo(() => [
    {
      name: 'projectID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectCode',
      label: 'Project Code',
      type: 'varchar',
      required: true,
      maxLength: 20,
      placeholder: 'e.g. AFIX',
    },
    {
      name: 'projectName',
      label: 'Project Name',
      type: 'varchar',
      required: true,
      maxLength: 150,
      placeholder: 'e.g. AFix Bug Tracker',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      maxLength: 500,
      placeholder: 'Enter project description...',
    },
    {
      name: 'clientOrgID',
      label: 'Client Organization',
      type: 'select',
      // Inactive organizations are not offered, except the one already selected
      options: clientOrgs
        .filter((org) => String(org.status || '').toLowerCase() !== 'inactive'
          || Number(org.clientOrgID) === Number(selectedRecord?.clientOrgID))
        .map((org) => ({ label: org.orgName, value: org.clientOrgID })),
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'Active',
      options: [
        { label: 'Active', value: 'Active' },
        { label: 'Inactive', value: 'Inactive' },
        { label: 'Archived', value: 'Archived' },
      ],
    },
    // Requirement #27: PM can fix the environment for a project so clients
    // don't select one. Only this project's environments are offered, so it
    // can be set once the project has environments (i.e. when editing).
    {
      name: 'defaultEnvironmentID',
      label: 'Fixed Environment',
      type: 'select',
      options: environments
        .filter((env) => selectedRecord && Number(env.projectID) === Number(selectedRecord.projectID))
        .map((env) => ({ label: env.environmentName, value: env.environmentID })),
    },
  ], [clientOrgs, environments, selectedRecord]);

  const columns = [
    { key: 'projectID', label: 'ID', sortable: true, width: '70px' },
    { key: 'projectCode', label: 'Code', sortable: true, width: '100px' },
    { key: 'projectName', label: 'Project Name', sortable: true },
    {
      key: 'clientOrgID',
      label: 'Client Organization',
      sortable: true,
      render: (row) => {
        const org = clientOrgs.find((o) => Number(o.clientOrgID) === Number(row.clientOrgID));
        return org ? org.orgName : '-';
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge value={row.status} />,
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Projects</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            All software development and bug tracking projects.
          </div>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            + New Project
          </button>
        )}
      </div>

      {/* Main projects grid */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="projectID"
        emptyMessage="No projects found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={canManage ? openEditModal : undefined}
        onDelete={canManage ? openDeleteModal : undefined}
        renderActions={(row) => (
          <>
            <button
              className="btn-table-action btn-table-action-primary"
              style={{ marginRight: '6px' }}
              onClick={() => navigate(`/projects/${row.projectID}`)}
            >
              Open
            </button>
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginRight: '6px', padding: '5px 10px', fontSize: '12px' }}
              onClick={() => navigate(`/project-teams?projectID=${row.projectID}`)}
              title="Manage Teams"
            >
              👥 Team
            </button>
          </>
        )}
      />

      {/* Reusable modal for Add / Edit */}
      {isFormOpen && (
        <TableForm
          fields={projectFields}
          initialValues={selectedRecord || { projectCode: '', projectName: '', description: '', clientOrgID: '', status: 'Active' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Project"
        />
      )}

      {/* Reusable modal for Delete */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Project"
        itemLabel={selectedRecord ? selectedRecord.projectName : 'Project'}
        deleting={deleting}
      />
    </div>
  );
}